import { useEffect } from 'react';
import { Select } from '@forecastify/ui';
import { useT } from '../../i18n';
import type { OvertimeSettlement } from '@forecastify/shared';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE THREE THINGS AN APPROVER DECIDES ABOUT AN OVERTIME CLAIM.        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * *Jóváhagyás módja* (paid or credited), *Melyik kvótára* (where a credit lands) and *Túlóra
 * típusa* (what a payout is priced as). Approving or rejecting is not here: this is the shape of
 * the decision, and the two surfaces that take one draw their own buttons.
 *
 * ⚠️⚠️ **IT IS ONE COMPONENT BECAUSE THERE ARE TWO SURFACES AND THEY MUST NOT DRIFT.** The rail in
 * `request-detail.tsx` had these controls and the row menu's approve had none at all — so `0147`'s
 * whole point, *the approver picks the kind*, was reachable only by opening the request first. The
 * dialog is the second surface (`decide-dialog.tsx`), and `task-detail.tsx` records the same rule
 * for the same reason: two copies diverge the moment one gains a field.
 *
 * ⚠️ **Controlled, with no state of its own.** Both callers already hold a draft — the rail keeps
 * one per open request, the dialog one per batch — and a component that owned the values would
 * have to be told when to forget them.
 */

export interface DecisionDraft {
  readonly settlement: OvertimeSettlement;
  readonly creditTypeId: string;
  /**
   * ⚠️ Empty is *"whatever the calendar says that day is"* — `typeForDay` on the server. An
   * opening position rather than a blank: an approver who changes nothing still prices correctly.
   */
  readonly payoutTypeId: string;
}

/** ⚠️ Payout, no destination and no named type — what approval has always meant. */
export const EMPTY_DECISION: DecisionDraft = {
  settlement: 'payout',
  creditTypeId: '',
  payoutTypeId: '',
};

export interface DecisionOptions {
  /** ⚠️ The quota-bearing types a credit may land in, the workspace's default first. */
  readonly creditTypes: readonly { readonly id: string; readonly name: string }[];
  /** ⚠️ The active overtime types, in the workspace's own order. */
  readonly overtimeTypes: readonly {
    readonly id: string;
    readonly name: string;
    readonly multiplier: string;
  }[];
}

/**
 * ⚠️ **Turns the draft into what `POST /overtime/decide` takes**, so neither caller assembles a
 * body by hand. Omitted rather than empty throughout: absent means *take the default*, which is
 * what keeps an untouched form behaving exactly as approval did before item 31 and `0147`.
 */
export function settleBody(draft: DecisionDraft): {
  settlement: OvertimeSettlement;
  creditLeaveTypeId?: string;
  overtimeTypeId?: string;
} {
  return {
    settlement: draft.settlement,
    ...(draft.settlement === 'credit' && draft.creditTypeId !== ''
      ? { creditLeaveTypeId: draft.creditTypeId }
      : {}),
    ...(draft.settlement === 'payout' && draft.payoutTypeId !== ''
      ? { overtimeTypeId: draft.payoutTypeId }
      : {}),
  };
}

export function DecisionControls({
  draft,
  options,
  onChange,
  compact = false,
}: {
  readonly draft: DecisionDraft;
  readonly options: DecisionOptions;
  readonly onChange: (next: DecisionDraft) => void;
  /** ⚠️ The rail is narrow and the dialog is not — the only difference between the two. */
  readonly compact?: boolean;
}) {
  const t = useT();
  const size = compact ? 'h-8 text-[12.5px]' : '';
  const patch = (next: Partial<DecisionDraft>): void => {
    onChange({ ...draft, ...next });
  };

  /**
   * ┌───────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ THE DESTINATION ARRIVES CHOSEN — the ruling of 20 Sep.                 │
   * └───────────────────────────────────────────────────────────────────┘
   *
   * ⚠️⚠️ **THE DRAFT STARTED EMPTY AND THE CONTROL LOOKED UNSET.** `creditTypeId` opened as
   * `''`, which matches no option, so the `Select` rendered blank and an approver had to pick a
   * value that was never in question. The server has defaulted since item 31 — `creditTargetFor`
   * takes the workspace's usual type when nothing is named — so the submit was already correct;
   * what was wrong was a control that hid its own answer and asked for it again.
   *
   * ⚠️ **An effect, because the list arrives LATER.** `decision-options.ts` reads the leave types
   * after mount, so there is nothing to pre-select at the moment either surface builds its draft.
   *
   * ⚠️ **It writes once.** The `!== ''` guard is what stops an effect that depends on the draft
   * from re-running against its own write.
   */
  const firstCreditType = options.creditTypes[0]?.id;
  useEffect(() => {
    if (firstCreditType === undefined) return;
    if (draft.creditTypeId !== '') return;
    onChange({ ...draft, creditTypeId: firstCreditType });
  }, [firstCreditType, draft, onChange]);

  /**
   * ⚠️⚠️ **ONE DESTINATION IS NOT A CHOICE, SO IT IS NOT OFFERED AS ONE.** The product
   * owner's wording: prefilled and unchangeable where the workspace has a single quota fed by
   * overtime, prefilled and changeable where it has several.
   *
   * ⚠️ **Shown rather than hidden, which is the opposite call to the settlement dropdown above.**
   * A one-option settlement picker would be furniture — it says nothing the default does not. This
   * says WHERE the days land, which is the thing an approver is accountable for, and a number
   * credited to an unnamed quota is a decision nobody can check afterwards.
   */
  const creditFixed = options.creditTypes.length === 1;

  return (
    <div className="flex flex-col gap-1.5">
      {/**
       * ⚠️⚠️ **KIFIZETÉS OR JÓVÁÍRÁS — feedback round 4 item 31.** Leave has one kind of approval;
       * this is the choice that makes overtime's two — the hours are paid, or they become leave
       * days.
       *
       * ⚠️ **Payout is pre-selected**, because it is what approval has always meant and because
       * `decide` defaults to it when nothing is said. Somebody who does not notice this control
       * gets the behaviour they had yesterday.
       *
       * ⚠️ **No destinations means there is no second way to settle**, and a dropdown whose only
       * option is the default is furniture. The rate picker below still shows.
       */}
      {options.creditTypes.length === 0 ? null : (
        <>
          <Select
            value={draft.settlement}
            aria-label={t('leave.settle.label')}
            className={size}
            onChange={(event) => {
              patch({ settlement: event.target.value as OvertimeSettlement });
            }}
            options={[
              { value: 'payout', label: t('leave.settle.payout') },
              { value: 'credit', label: t('leave.settle.credit') },
            ]}
          />
          {/* ⚠️ The destination is a DEFAULT the approver can change — the product owner's own
              wording. The list arrives with the workspace's usual type first. */}
          {draft.settlement !== 'credit' ? null : (
            <Select
              value={draft.creditTypeId}
              aria-label={t('leave.settle.creditTo')}
              className={size}
              /* ⚠️ Nothing to choose between — see `creditFixed`. */
              disabled={creditFixed}
              onChange={(event) => {
                patch({ creditTypeId: event.target.value });
              }}
              options={options.creditTypes.map((type) => ({ value: type.id, label: type.name }))}
            />
          )}
        </>
      )}

      {/**
       * ┌─────────────────────────────────────────────────────────────────────┐
       * │ ⚠️⚠️ 1.5×, 2× OR 2.5× — AND IT IS DECIDED HERE. `0147`.             │
       * └─────────────────────────────────────────────────────────────────────┘
       *
       * The requester asserted that an hour was overtime. What KIND it was — and so what it is
       * worth — is this person's call, and the multiplier freezes off whatever they choose at the
       * moment they approve.
       *
       * ⚠️ **The day's own answer is the first option and the pre-selected one**, so a decision
       * nobody thinks about still prices correctly: a Saturday takes the weekend type, a holiday
       * the holiday one. That is `0111`'s rule as an opening position rather than an answer, and
       * it is what keeps a thirty-claim batch from asking thirty questions.
       *
       * ⚠️ **A credit prices nothing**, so the picker goes when the settlement does.
       */}
      {draft.settlement === 'credit' || options.overtimeTypes.length === 0 ? null : (
        <Select
          value={draft.payoutTypeId}
          aria-label={t('leave.settle.rate')}
          className={size}
          onChange={(event) => {
            patch({ payoutTypeId: event.target.value });
          }}
          options={[
            { value: '', label: t('leave.settle.rateFromDay') },
            ...options.overtimeTypes.map((type) => ({
              value: type.id,
              /* ⚠️ The rate on the option, because this IS the question about money. */
              label: `${type.name} · ${String(Number(type.multiplier))}×`,
            })),
          ]}
        />
      )}

      {/* ⚠️⚠️ **THE CREDIT BRANCH IS STRUCK** — *"nincs szorzó és nincs kifizetés…"* was a
          paragraph explaining the option the reader had just chosen. The payout line survives it
          because it says where the money GOES, which is not on the control. */}
      {options.creditTypes.length === 0 || draft.settlement === 'credit' ? null : (
        <p className="text-muted-foreground text-[11.5px] leading-relaxed">
          {t('leave.settle.payoutHint')}
        </p>
      )}
    </div>
  );
}
